import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const FIELD_LABELS = {
  property_status_log: "Property Status Log",
  deal_owner: "Deal Owner",
  property_contact: "Property Contact",
  pss_comments: "PSS Comments",
  is_digitalfranchise: "Digital Franchise",
  booking_sms_notif: "Booking SMS Notification",
  booking_whatsapp_notif: "Booking WhatsApp Notification",
  enable_id_scanner: "ID Scanner",
  enable_gha: "Google Hotel Ads (GHA)",
  enable_bookflexi: "BookFlexi",
  pg_account_type: "Payment Gateway Account Type",
  pg_transfer_on: "Payment Gateway Transfer",
  sub_logs: "Subscription Logs",
  disable_banner: "Disable Banner",
  flexi_ai: "Flexi AI",
  hotel_name: "Hotel Name",
  hotel_email: "Hotel Email",
  hotel_phone: "Hotel Phone",
  hotel_address: "Hotel Address",
  hotel_city: "Hotel City",
  hotel_state: "Hotel State",
  hotel_country: "Hotel Country",
  hotel_zipcode: "Hotel Zipcode",
  hotel_currency: "Hotel Currency",
  hotel_timezone: "Hotel Timezone",
  hotel_logo: "Hotel Logo",
  hotel_website: "Hotel Website",
  checkin_time: "Check-in Time",
  checkout_time: "Check-out Time",
  tax_percentage: "Tax Percentage",
  tax_name: "Tax Name",
  room_count: "Room Count",
  property_type: "Property Type",
  star_rating: "Star Rating",
  channel_manager: "Channel Manager",
  booking_engine: "Booking Engine",
  payment_gateway: "Payment Gateway",
  is_active: "Active Status",
  is_live: "Live Status",
  enable_expedia: "Expedia",
  enable_booking: "Booking.com",
  enable_airbnb: "Airbnb",
  enable_tripadvisor: "TripAdvisor",
};

const VALUE_LABELS = {
  ENABLE: "Enabled",
  DISABLE: "Disabled",
  ENABLED: "Enabled",
  DISABLED: "Disabled",
  YES: "Yes",
  NO: "No",
  true: "Yes",
  false: "No",
  DIRECT_CHARGE: "Direct Charge",
  IMMEDIATE: "Immediate",
  DESTINATION: "Destination",
  DELAYED: "Delayed",
};

function getFieldLabel(fieldName) {
  return FIELD_LABELS[fieldName] || fieldName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function getValueLabel(value) {
  if (value == null) return "(empty)";
  const trimmed = String(value).trim();
  if (trimmed === "") return "(empty)";
  return VALUE_LABELS[trimmed] || trimmed;
}

function parseLogJson(logText) {
  if (!logText || typeof logText !== "string") return null;

  const trimmed = logText.trim();
  if (!trimmed.startsWith("{")) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function extractDiffData(parsed) {
  if (!parsed || typeof parsed !== "object") return null;

  const keys = Object.keys(parsed);
  const oldDataKey = keys.find((k) => k.toLowerCase().includes("old_data"));
  const newDataKey = keys.find((k) => k.toLowerCase().includes("new_data"));

  if (!oldDataKey || !newDataKey) return null;

  const actionLabel = oldDataKey
    .replace(/[-_]?\s*old_data\s*/i, "")
    .replace(/\s*-\s*$/, "")
    .trim();

  return {
    action: actionLabel || "Data Updated",
    oldData: parsed[oldDataKey],
    newData: parsed[newDataKey],
  };
}

function computeChanges(oldData, newData) {
  if (!oldData || !newData) return [];

  const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
  const changes = [];

  allKeys.forEach((key) => {
    const oldVal = String(oldData[key] ?? "");
    const newVal = String(newData[key] ?? "");

    if (oldVal !== newVal) {
      if (key === "sub_logs") {
        const oldEntries = oldVal.split(",").map((s) => s.trim()).filter(Boolean);
        const newEntries = newVal.split(",").map((s) => s.trim()).filter(Boolean);
        const addedEntries = newEntries.filter((e) => !oldEntries.includes(e));
        changes.push({
          field: key,
          type: "list_append",
          addedEntries,
          oldValue: oldVal,
          newValue: newVal,
        });
      } else {
        changes.push({
          field: key,
          type: "value_change",
          oldValue: oldVal,
          newValue: newVal,
        });
      }
    }
  });

  return changes;
}

function ChangeBadge({ oldValue, newValue }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Chip
        label={getValueLabel(oldValue)}
        size="small"
        sx={{
          bgcolor: "#ffebee",
          color: "#c62828",
          fontWeight: 500,
          textDecoration: "line-through",
          maxWidth: 300,
          "& .MuiChip-label": { whiteSpace: "normal", wordBreak: "break-word" },
        }}
      />
      <ArrowForwardIcon sx={{ fontSize: 16, color: "#666" }} />
      <Chip
        label={getValueLabel(newValue)}
        size="small"
        sx={{
          bgcolor: "#e8f5e9",
          color: "#2e7d32",
          fontWeight: 500,
          maxWidth: 300,
          "& .MuiChip-label": { whiteSpace: "normal", wordBreak: "break-word" },
        }}
      />
    </Box>
  );
}

function ChangeRow({ change }) {
  const [expanded, setExpanded] = useState(false);

  if (change.type === "list_append") {
    return (
      <Box sx={{ py: 1, px: 2, "&:hover": { bgcolor: "#fafafa" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#333", minWidth: 180 }}>
            {getFieldLabel(change.field)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={`+${change.addedEntries.length} new ${change.addedEntries.length === 1 ? "entry" : "entries"}`}
              size="small"
              sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 500 }}
            />
            <IconButton size="small" onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={expanded}>
          <Box sx={{ mt: 1, pl: 2 }}>
            {change.addedEntries.map((entry, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{
                  color: "#2e7d32",
                  py: 0.25,
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                }}
              >
                + {entry}
              </Typography>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1, px: 2, "&:hover": { bgcolor: "#fafafa" } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: "#333", minWidth: 180 }}>
          {getFieldLabel(change.field)}
        </Typography>
        <ChangeBadge oldValue={change.oldValue} newValue={change.newValue} />
      </Box>
    </Box>
  );
}

function DiffView({ action, changes }) {
  const [showAll, setShowAll] = useState(false);
  const visibleChanges = showAll ? changes : changes.slice(0, 10);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, px: 2 }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: "#1976d2" }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1976d2" }}>
          {action}
        </Typography>
        <Chip
          label={`${changes.length} ${changes.length === 1 ? "change" : "changes"}`}
          size="small"
          variant="outlined"
          sx={{ ml: "auto", fontWeight: 500 }}
        />
      </Box>
      <Divider />
      {visibleChanges.map((change, i) => (
        <React.Fragment key={change.field}>
          <ChangeRow change={change} />
          {i < visibleChanges.length - 1 && <Divider sx={{ mx: 2 }} />}
        </React.Fragment>
      ))}
      {changes.length > 10 && (
        <Box sx={{ textAlign: "center", py: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll ? "Show less" : `Show all ${changes.length} changes`}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function RawLogView({ logText }) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "#f5f5f5",
        borderRadius: 1,
        fontFamily: "monospace",
        fontSize: "0.85rem",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: 400,
        overflow: "auto",
      }}
    >
      {logText}
    </Box>
  );
}

export default function LogDiffViewer({ logText, compact = false }) {
  const [showRaw, setShowRaw] = useState(false);

  if (!logText) return <Typography color="text.secondary">No log data</Typography>;

  const parsed = parseLogJson(logText);
  const diffData = parsed ? extractDiffData(parsed) : null;
  const changes = diffData ? computeChanges(diffData.oldData, diffData.newData) : [];

  if (!diffData || changes.length === 0) {
    if (parsed && !diffData) {
      return <RawLogView logText={JSON.stringify(parsed, null, 2)} />;
    }
    if (diffData && changes.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {diffData.action} — no field changes detected
          </Typography>
        </Box>
      );
    }
    return <RawLogView logText={logText} />;
  }

  if (compact) {
    return (
      <Typography variant="body2" sx={{ color: "#333" }}>
        <strong>{diffData.action}:</strong>{" "}
        {changes.map((c) => getFieldLabel(c.field)).join(", ")}
      </Typography>
    );
  }

  return (
    <Box>
      <DiffView action={diffData.action} changes={changes} />
      <Box sx={{ textAlign: "right", px: 2, pt: 1 }}>
        <Typography
          variant="caption"
          sx={{ color: "#999", cursor: "pointer", "&:hover": { color: "#666" } }}
          onClick={() => setShowRaw((prev) => !prev)}
        >
          {showRaw ? "Hide raw log" : "Show raw log"}
        </Typography>
      </Box>
      <Collapse in={showRaw}>
        <Box sx={{ px: 2, pb: 1 }}>
          <RawLogView logText={logText} />
        </Box>
      </Collapse>
    </Box>
  );
}

export function getLogSummary(logText) {
  if (!logText || typeof logText !== "string") return logText;

  const parsed = parseLogJson(logText);
  if (!parsed) return logText;

  const diffData = extractDiffData(parsed);
  if (!diffData) return logText;

  const changes = computeChanges(diffData.oldData, diffData.newData);
  if (changes.length === 0) return `${diffData.action} (no changes)`;

  const fieldNames = changes.map((c) => getFieldLabel(c.field));
  return `${diffData.action}: ${fieldNames.join(", ")}`;
}
